import { useEffect, useState } from 'react';
import md5 from 'js-md5';
import WeeklyTimeTrackingWidget from './components/WeeklyTimeTrackingWidget';
import AuthWrapper from './components/AuthWrapper';
import ErrorLog from './components/ErrorLog';
import { createAsanaClient } from './asanaClient';
import { makePkcePair } from './pkce';
import { API_BASE, CLIENT_ID, REDIRECT_URI } from './constants';

// Debug flag for local development
const DEBUG_MODE = process.env.NODE_ENV === 'development';
const TEST_TOKEN = DEBUG_MODE
	? '2/1201665068868328/1210080933979550:a026d113c73f70b8f61e3e1b28159dfa'
	: '';

export default function App() {
	const [token, setToken] = useState(
		() => localStorage.getItem('asana_access') || (DEBUG_MODE ? TEST_TOKEN : '')
	);
	const [refreshToken, setRefreshToken] = useState(
		() => localStorage.getItem('asana_refresh') || ''
	);
	const [asana, setAsana] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [debugMode, setDebugMode] = useState(DEBUG_MODE);

	// Toggle debug mode (only in development)
	const toggleDebugMode = () => {
		if (process.env.NODE_ENV === 'development') {
			setDebugMode(!debugMode);
			if (!debugMode && TEST_TOKEN) {
				setToken(TEST_TOKEN);
			}
		}
	};

	// Handle OAuth callback or start OAuth flow
	useEffect(() => {
		const handleOAuthFlow = async () => {
			try {
				setLoading(true);

				// Skip OAuth flow if we're in debug mode with a test token
				if (debugMode && TEST_TOKEN) {
					setToken(TEST_TOKEN);
					setLoading(false);
					return;
				}

				const urlParams = new URLSearchParams(window.location.search);
				const authCode = urlParams.get('code');

				// If we have a code from OAuth callback
				if (authCode) {
					const verifier = localStorage.getItem('pkce_verifier');
					if (!verifier) {
						throw new Error('PKCE verifier not found');
					}

					const resp = await fetch(`${API_BASE}/_oauth_token.php`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ code: authCode, code_verifier: verifier }),
					});

					if (!resp.ok) {
						throw new Error(`Token exchange failed: ${resp.status}`);
					}

					const data = await resp.json();
					if (data.access_token) {
						localStorage.setItem('asana_access', data.access_token);
						if (data.refresh_token) {
							localStorage.setItem('asana_refresh', data.refresh_token);
							setRefreshToken(data.refresh_token);
						}
						setToken(data.access_token);
						window.history.replaceState({}, '', '/'); // Remove ?code from URL
					} else {
						throw new Error('No access token in response');
					}
				}
				// If no token, start OAuth flow
				else if (!token) {
					const { verifier, challenge } = await makePkcePair();
					localStorage.setItem('pkce_verifier', verifier);

					const qs = new URLSearchParams({
						client_id: CLIENT_ID,
						redirect_uri: REDIRECT_URI,
						response_type: 'code',
						code_challenge: challenge,
						code_challenge_method: 'S256',
						scope: 'default',
					});
					window.location.href = `https://app.asana.com/-/oauth_authorize?${qs.toString()}`;
				}
			} catch (err) {
				console.error('OAuth flow error:', err);
				setError(`Authentication error: ${err.message}`);
			} finally {
				setLoading(false);
			}
		};

		handleOAuthFlow();
	}, [token, debugMode]);

	// Initialize Asana client when token is available
	useEffect(() => {
		if (!token) return;

		const initAsanaClient = async () => {
			try {
				const client = createAsanaClient(token);

				// Get user info from token
				// Simplified approach - use the token hash as user ID
				const userId = token ? md5(token) : null;
				if (userId) {
					localStorage.setItem('asana_user_id', userId);
				}

				setAsana({
					client,
					users: client.users,
					workspaces: client.workspaces,
					tasks: client.tasks,
					projects: client.projects,
					timeTrackingEntries: {
						async getTimeTrackingEntriesForTask(gid, { opt_fields } = {}) {
							try {
								const url =
									`${API_BASE}/_timeentries.php?gid=${gid}` +
									(opt_fields
										? `&opt_fields=${encodeURIComponent(opt_fields)}`
										: '');

								const headers = {
									'Access-Token': token,
									'Refresh-Token': refreshToken,
								};

								// Add User-ID header if available
								if (userId) {
									headers['User-ID'] = userId;
								}

								const r = await fetch(url, { headers });

								// Check if server sent us new tokens
								const newAccessToken = r.headers.get('New-Access-Token');
								const newRefreshToken = r.headers.get('New-Refresh-Token');

								// Update tokens if provided
								if (newAccessToken) {
									localStorage.setItem('asana_access', newAccessToken);
									setToken(newAccessToken);
								}

								if (newRefreshToken) {
									localStorage.setItem('asana_refresh', newRefreshToken);
									setRefreshToken(newRefreshToken);
								}

								if (r.status === 401) {
									throw new Error('Authentication failed');
								}

								const responseData = await r.json();

								// Add response to error log if it contains errors
								if (responseData.errors || r.status >= 400) {
									const errorDetails = {
										timestamp: new Date().toISOString(),
										endpoint: url,
										status: r.status,
										response: responseData,
									};

									// Store in session storage, allowing it to persist during the session
									const currentErrors = JSON.parse(
										sessionStorage.getItem('asana_errors') || '[]'
									);
									currentErrors.push(errorDetails);
									sessionStorage.setItem(
										'asana_errors',
										JSON.stringify(currentErrors)
									);
								}

								return responseData;
							} catch (err) {
								console.error('Error fetching time entries:', err);

								// Add to error log
								const errorDetails = {
									timestamp: new Date().toISOString(),
									endpoint: `${API_BASE}/_timeentries.php?gid=${gid}`,
									error: err.message,
								};

								const currentErrors = JSON.parse(
									sessionStorage.getItem('asana_errors') || '[]'
								);
								currentErrors.push(errorDetails);
								sessionStorage.setItem(
									'asana_errors',
									JSON.stringify(currentErrors)
								);

								throw err;
							}
						},
					},
				});
			} catch (err) {
				console.error('Failed to initialize Asana client:', err);
				setError('Failed to initialize Asana client');

				// Add to error log
				const errorDetails = {
					timestamp: new Date().toISOString(),
					component: 'AsanaClient',
					error: err.message,
				};

				const currentErrors = JSON.parse(
					sessionStorage.getItem('asana_errors') || '[]'
				);
				currentErrors.push(errorDetails);
				sessionStorage.setItem('asana_errors', JSON.stringify(currentErrors));

				// Clear tokens on unrecoverable errors
				if (
					!debugMode &&
					(err.message?.includes('unauthorized') ||
						err.message?.includes('401'))
				) {
					localStorage.removeItem('asana_access');
					localStorage.removeItem('asana_refresh');
					setToken('');
					setRefreshToken('');
				}
			}
		};

		initAsanaClient();
	}, [token, refreshToken, debugMode]);

	// Development mode controls
	const renderDevControls = () => {
		if (process.env.NODE_ENV !== 'development') return null;

		return (
			<div className='fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-3 flex justify-between items-center'>
				<div>
					<label className='inline-flex items-center'>
						<input
							type='checkbox'
							checked={debugMode}
							onChange={toggleDebugMode}
							className='mr-2'
						/>
						Debug Mode
					</label>
				</div>
				<div className='flex gap-2'>
					<button
						className='px-3 py-1 bg-red-600 rounded text-sm'
						onClick={() => {
							localStorage.removeItem('asana_access');
							localStorage.removeItem('asana_refresh');
							localStorage.removeItem('pkce_verifier');
							localStorage.removeItem('isAuth'); // Also clear auth status
							window.location.reload();
						}}
					>
						Clear Auth
					</button>
					<input
						type='text'
						placeholder='Set test token'
						className='px-2 py-1 rounded text-black text-sm w-64'
						value={token.substring(0, 20) + '...'}
						onChange={e => {
							const newToken = e.target.value;
							setToken(newToken);
							if (newToken) localStorage.setItem('asana_access', newToken);
						}}
					/>
				</div>
			</div>
		);
	};

	if (error) {
		return (
			<div className='p-6 bg-red-50 text-red-700 rounded-md'>
				<h2 className='text-xl font-bold mb-2'>Error</h2>
				<p>{error}</p>
				<button
					className='mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
					onClick={() => {
						localStorage.removeItem('asana_access');
						localStorage.removeItem('asana_refresh');
						localStorage.removeItem('pkce_verifier');
						window.location.reload();
					}}
				>
					Restart Authentication
				</button>
				{renderDevControls()}
			</div>
		);
	}

	if (loading || !asana) {
		return (
			<div className='p-8 text-center'>
				<div className='animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4'></div>
				<p className='text-lg text-gray-700'>Authenticating with Asana...</p>
				{renderDevControls()}
			</div>
		);
	}

	return (
		<>
			<AuthWrapper>
				<WeeklyTimeTrackingWidget asana={asana} />
			</AuthWrapper>
			<ErrorLog />
			{renderDevControls()}
		</>
	);
}
